import { Timestamp } from 'firebase/firestore';
import { MasterMappingType, MasterType } from '../types/master';
import { PatternType } from '../types/pattern';

/**
 * マスタ一覧の表示順を決めるユーティリティ（#3135）。
 *
 * 従来は Firestore の updated_at 降順だったため、CSV更新やマッピング編集のたびに
 * 行の位置が入れ替わり、目的のマスタを探しづらかった。
 *
 * 並び順のキーは次の優先度で決まる。
 *   1. display_order 昇順（ドラッグで並べ替えたマスタのみ設定される）
 *   2. 代表パターンの作成日 降順（同じパターンのマスタを隣接させる）
 *   3. マスタの作成日 昇順（アップロードした順）
 *
 * 「代表パターン」は、そのマスタに最初に紐づけたパターンを指す。
 *
 * display_order は任意フィールドのため、未設定の既存マスタは 2 以降にフォールバックする。
 * 既存データの移行は不要。
 */

/** パターンの作成日をミリ秒で引くためのマップ。pattern_id をキーにする。 */
export type PatternCreatedAtMap = { [patternId: string]: number };

/** マッピング未設定のマスタは一覧の末尾へ回すための番兵。降順比較で必ず最小になる値。 */
const UNMAPPED_SORT_KEY = Number.NEGATIVE_INFINITY;

/** Timestamp をミリ秒に変換する。未設定は最古（0）扱い。 */
const toMillis = (timestamp: Timestamp | undefined): number => {
  return timestamp ? timestamp.toMillis() : 0;
};

/** パターン一覧から pattern_id → 作成日ミリ秒 のマップを作る。 */
export const buildPatternCreatedAtMap = (patterns: PatternType[]): PatternCreatedAtMap => {
  return patterns.reduce<PatternCreatedAtMap>((map, pattern) => {
    map[pattern.patternId] = toMillis(pattern.created_at);
    return map;
  }, {});
};

/**
 * マスタの代表パターンの作成日（ミリ秒）を返す。
 *
 * 1つのマスタが複数パターンに紐づくケースがあるが、一覧の行はマスタごとに1行のため、
 * 「最初に紐づけたパターン」を代表として採用する。
 * マッピングは作成日の昇順で取得される（mapping/read.ts）ため、先頭が1件目にあたる。
 * マッピングは物理削除のため、1件目を削除すると2件目が自動的に代表へ繰り上がる。
 *
 * マッピング未設定のマスタ、および紐づくパターンが取得できない場合は番兵を返し、
 * 一覧の末尾に並ぶ。
 */
export const getRepresentativePatternCreatedAt = (
  mappings: MasterMappingType[],
  patternCreatedAtMap: PatternCreatedAtMap,
): number => {
  // 削除済みパターンなど、パターン一覧に存在しないものは代表から除外する
  const firstMapping = mappings.find(
    mapping => patternCreatedAtMap[mapping.pattern_id] !== undefined,
  );
  if (!firstMapping) return UNMAPPED_SORT_KEY;
  return patternCreatedAtMap[firstMapping.pattern_id];
};

/**
 * マスタ一覧を表示順で並べ替えた新しい配列を返す。引数の配列は変更しない。
 *
 * display_order が設定されたマスタを昇順で先に並べ、未設定のマスタは
 * 「代表パターンの作成日 降順 → マスタの作成日 昇順」で後ろに続ける。
 * created_at は登録後に変わらないため、CSV更新やマッピング編集では行の位置が動かない。
 *
 * @param masters 並べ替え対象のマスタ一覧
 * @param mappingsByMaster master_id をキーにしたマッピング一覧。未取得の master は未設定扱い。
 * @param patternCreatedAtMap pattern_id をキーにした作成日ミリ秒のマップ。
 */
export const sortMastersByPattern = (
  masters: MasterType[],
  mappingsByMaster: { [masterId: string]: MasterMappingType[] },
  patternCreatedAtMap: PatternCreatedAtMap,
): MasterType[] => {
  return [...masters].sort((a, b) => {
    // 1. display_order 昇順（未設定は末尾）
    const orderA = a.display_order ?? Number.MAX_SAFE_INTEGER;
    const orderB = b.display_order ?? Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) return orderA - orderB;

    // 2. 代表パターンの作成日 降順
    const patternA = getRepresentativePatternCreatedAt(
      mappingsByMaster[a.master_id] ?? [],
      patternCreatedAtMap,
    );
    const patternB = getRepresentativePatternCreatedAt(
      mappingsByMaster[b.master_id] ?? [],
      patternCreatedAtMap,
    );
    if (patternA !== patternB) return patternB - patternA;

    // 3. マスタの作成日 昇順（先に登録したマスタほど上）
    return toMillis(a.created_at) - toMillis(b.created_at);
  });
};
